import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowRight,
  IconChevronDown,
  IconChevronRight,
  IconDownload,
  IconFileText,
  IconRefresh,
} from '@tabler/icons-react';
import { useHelpTokens } from '@/features/help/useHelpTokens';
import {
  FACTORY_STORE_INFO,
  FACTORY_SUBMISSION_STATE_LABELS,
  FACTORY_SUBMISSION_STATE_COLORS,
  type FactoryStoreKey,
} from '@/lib/constants/appFactory';
import type { AppCfgsDto } from '@/lib/types/App';
import type { FactoryBuild, FactoryStoreAccount, FactorySubmission } from '@/lib/types/AppFactory';
import { MarkdownPreview } from '@/shared/components/MarkdownField/MarkdownPreview';
import { AppFactoryUtils } from '../AppFactoryUtils';
import { StoreAccountUtils } from '../StoreAccountUtils';
import {
  GUIDE_REFRESH_FROM_STATE,
  GUIDE_REFRESH_NOTE,
  GUIDE_REFRESH_TO_STATE,
  SubmissionGuideUtils,
} from '../SubmissionGuideUtils';
import { useAppRokuFeed } from '../useAppPreview';
import {
  useIsFactoryOperator,
  useSubmissionDownloads,
  useTransitionOwnSubmission,
} from '../useAppFactory';

/**
 * The canonical happy path a store submission walks. Rejected/superseded are
 * terminal exceptions: they replace the tail of the trail when they ARE the
 * current state and are never shown as upcoming steps.
 */
const SUBMISSION_TRAIL = [
  'preparing',
  'awaiting_customer',
  'submitting',
  'in_review',
  'approved',
  'live',
] as const;

const NOTE_MAX = 500;

interface AppSubmissionDetailProps {
  submission: FactorySubmission;
  appCfgId: number;
  app: AppCfgsDto;
  /** The build this submission packages (its artifact gates the package button, B5). */
  build?: FactoryBuild | null;
  buildSemver?: string;
  /** The store account covering this app on the submission's store (B1); null = none yet. */
  storeAccount?: FactoryStoreAccount | null;
}

/**
 * Tier 1 self-serve submission detail (post-JP sprint 2.1, ring-2 readiness
 * B1/B3/B5/B6/B7): everything the person driving the store portal needs, in
 * one card — who submits, the state trail with what the state means, presigned
 * pkg + guide downloads (only once they exist), build + shell version, the
 * portal walkthrough, the frozen deep-link test values, the portal-entry
 * checklist with gap warnings, the recorded steps, and — when the account
 * holder submits itself — the control to record the next portal step. Since
 * 3.34.2 a Roku card at Ready to submit also checks its frozen deep-link
 * values against the app's own feed and offers "Refresh the store guide"
 * (the awaiting_customer -> preparing request; appfactory ADR-0035).
 */
export function AppSubmissionDetail({
  submission,
  appCfgId,
  app,
  build,
  buildSemver,
  storeAccount = null,
}: AppSubmissionDetailProps) {
  const downloads = useSubmissionDownloads(appCfgId);
  const transition = useTransitionOwnSubmission(appCfgId);
  const { operatorName } = useHelpTokens();
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const [nextState, setNextState] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const openArtifact = (kind: 'pkg' | 'doc') => {
    downloads.mutate(submission.appSubmissionId, {
      onSuccess: (res) => {
        const url = kind === 'pkg' ? res.pkgUrl : res.submissionDocUrl;
        if (url) window.open(url, '_blank', 'noopener');
        else
          notifications.show({
            title: 'Link unavailable',
            message: 'The file could not be located right now. Try again in a moment.',
            color: 'orange',
          });
      },
      onError: (err) =>
        notifications.show({ title: 'Error', message: err.message, color: 'red' }),
    });
  };

  const deeplinks = submission.deeplinkValues ?? [];
  const listing = app.listing;
  const storeInfo = FACTORY_STORE_INFO[submission.store as FactoryStoreKey];
  const storeLabel = storeInfo?.label ?? submission.store;
  const isRoku = submission.store === 'roku';
  const isOperator = useIsFactoryOperator();
  // The guide's freshness (3.34.2): the app's feed is the deep-link derive's own
  // source, read only while a Roku submission waits on the holder.
  const refreshable = SubmissionGuideUtils.refreshable(submission, submission.store);
  const feedQuery = useAppRokuFeed(app.roku?.core.feedUrl ?? null, refreshable);
  const staleWarning = SubmissionGuideUtils.staleWarning(
    SubmissionGuideUtils.staleValues(deeplinks, feedQuery.data),
  );

  // Portal-entry checklist: what the store portal asks for, where we hold a
  // value, and what is still blank. Genres (Roku) + app-ads.txt are chosen/hosted
  // outside the dashboard, so their gaps are informational, not blocking.
  const checklist: { label: string; value: string | null; hint?: string }[] = [
    { label: 'Privacy Policy URL', value: listing?.privacyPolicyUrl ?? null },
    { label: 'Terms of Use URL', value: listing?.termsUrl ?? null },
    { label: 'Age Rating', value: listing?.ageRating ?? null },
    { label: 'Support Email', value: listing?.supportEmail ?? null },
    { label: 'Support Phone', value: listing?.supportPhone ?? null },
    { label: 'Support URL', value: listing?.supportUrl ?? null },
    {
      label: 'app-ads.txt URL',
      value: listing?.appAdsTxtUrl ?? null,
      hint: 'Only needed for ad-enabled apps',
    },
    ...(isRoku
      ? [{ label: 'Genres', value: app.roku?.core.genres ?? null, hint: 'Chosen in the Roku portal' }]
      : []),
  ];
  const gaps = checklist.filter((c) => !c.value);

  const isException = submission.state === 'rejected' || submission.state === 'superseded';
  const trailIdx = SUBMISSION_TRAIL.indexOf(
    submission.state as (typeof SUBMISSION_TRAIL)[number],
  );

  const canPkg = StoreAccountUtils.canDownloadPkg(build);
  const canDoc = StoreAccountUtils.canDownloadDoc(submission);
  // B3: the holder records its own portal steps; the operator uses the console.
  const holderSubmits = storeAccount?.state === 'active' && storeAccount.submitter === 'holder';
  const holderNext = holderSubmits ? StoreAccountUtils.holderSubmissionNext(submission.state) : [];
  const canRefresh = refreshable && (holderSubmits || isOperator);
  const events = [...(submission.events ?? [])].sort((a, b) =>
    a.created < b.created ? -1 : a.created > b.created ? 1 : 0,
  );

  const recordStep = () => {
    if (!nextState) return;
    transition.mutate(
      { submissionId: submission.appSubmissionId, from: submission.state, to: nextState, note: note.trim() || undefined },
      {
        onSuccess: () => {
          setNextState(null);
          setNote('');
          notifications.show({
            title: 'Step recorded',
            message: `${FACTORY_SUBMISSION_STATE_LABELS[nextState] ?? nextState} recorded for this submission.`,
            color: 'green',
          });
        },
        onError: (err) =>
          notifications.show({ title: 'Error', message: err.message, color: 'red' }),
      },
    );
  };

  const refreshGuide = () => {
    transition.mutate(
      {
        submissionId: submission.appSubmissionId,
        from: GUIDE_REFRESH_FROM_STATE,
        to: GUIDE_REFRESH_TO_STATE,
        note: GUIDE_REFRESH_NOTE,
      },
      {
        onSuccess: () =>
          notifications.show({
            title: 'Refreshing the guide',
            message:
              'The farm re-tests the deep links on a device; the values and the guide update here in about a minute.',
            color: 'blue',
          }),
        onError: (err) =>
          notifications.show({ title: 'Refresh not started', message: err.message, color: 'red' }),
      },
    );
  };

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="md">
        {/* Header: state + version + dates */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <Badge
              variant="light"
              color={FACTORY_SUBMISSION_STATE_COLORS[submission.state] ?? 'gray'}
            >
              {FACTORY_SUBMISSION_STATE_LABELS[submission.state] ?? submission.state}
            </Badge>
            <Text fw={600} size="sm">
              {storeLabel}
              {buildSemver ? ` · v${buildSemver}` : ''}
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Prepared {AppFactoryUtils.formatWhen(submission.created)}
            {submission.submittedAt
              ? ` · Submitted ${AppFactoryUtils.formatWhen(submission.submittedAt)}`
              : ''}
            {submission.decidedAt
              ? ` · Decided ${AppFactoryUtils.formatWhen(submission.decidedAt)}`
              : ''}
          </Text>
        </Group>

        {/* B1: what this state means + who submits */}
        <Stack gap={2}>
          <Text size="sm">{StoreAccountUtils.stateGuidance(submission.state, submission.store)}</Text>
          <Text size="sm" c="dimmed">
            {StoreAccountUtils.whoSubmits(storeAccount, operatorName, storeLabel, submission)}
          </Text>
        </Stack>

        {/* State trail */}
        <Group gap={6} wrap="wrap">
          {SUBMISSION_TRAIL.map((s, i) => {
            const reached = trailIdx >= 0 && i <= trailIdx;
            const current = s === submission.state;
            return (
              <Group gap={6} key={s} wrap="nowrap">
                {i > 0 && <IconArrowRight size={12} color="var(--mantine-color-gray-5)" />}
                <Badge
                  size="sm"
                  variant={current ? 'filled' : 'light'}
                  color={current || reached ? (FACTORY_SUBMISSION_STATE_COLORS[s] ?? 'blue') : 'gray'}
                  style={reached || current ? undefined : { opacity: 0.5 }}
                >
                  {FACTORY_SUBMISSION_STATE_LABELS[s] ?? s}
                </Badge>
              </Group>
            );
          })}
          {isException && (
            <Group gap={6} wrap="nowrap">
              <IconArrowRight size={12} color="var(--mantine-color-gray-5)" />
              <Badge
                size="sm"
                variant="filled"
                color={FACTORY_SUBMISSION_STATE_COLORS[submission.state] ?? 'gray'}
              >
                {FACTORY_SUBMISSION_STATE_LABELS[submission.state] ?? submission.state}
              </Badge>
            </Group>
          )}
        </Group>

        {/* Recorded steps (who moved it where) */}
        {events.length > 0 && (
          <Stack gap={2}>
            {events.map((e) => (
              <Text size="xs" c="dimmed" key={e.eventId}>
                {AppFactoryUtils.formatWhen(e.created)} · {FACTORY_SUBMISSION_STATE_LABELS[e.toState] ?? e.toState}
                {' '}recorded by {e.actorKind === 'operator' ? operatorName : e.actorKind === 'system' ? 'the build system' : e.actorRef || e.actorKind}
                {e.note ? ` — ${e.note}` : ''}
              </Text>
            ))}
          </Stack>
        )}

        {/* B3: record the next portal step (holder-submitted accounts only) */}
        {holderNext.length > 0 && (
          <Group align="flex-end" gap="sm">
            <Select
              label="Record a portal step"
              placeholder="What happened in the portal?"
              data={holderNext.map((s) => ({ value: s, label: FACTORY_SUBMISSION_STATE_LABELS[s] ?? s }))}
              value={nextState}
              onChange={setNextState}
              w={220}
            />
            <TextInput
              label="Note"
              placeholder="Optional — e.g. the store's reference"
              maxLength={NOTE_MAX}
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button onClick={recordStep} disabled={!nextState} loading={transition.isPending}>
              Record
            </Button>
          </Group>
        )}

        {/* B6: build identity + supersede rule */}
        <Text size="xs" c="dimmed">
          Build {submission.appBuild2Id}
          {buildSemver ? ` · shell v${buildSemver}` : ''}
          {build?.versionCode != null ? ` · build number ${build.versionCode}` : ''} — a newer build
          for this app supersedes this submission.
        </Text>

        {/* Downloads (B5: only what exists) */}
        {(canPkg || canDoc) && (
          <Group gap="sm">
            {canPkg && (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconDownload size={14} />}
                loading={downloads.isPending}
                onClick={() => openArtifact('pkg')}
              >
                {storeInfo?.packageLabel ?? 'Download package'}
              </Button>
            )}
            {canDoc && (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconFileText size={14} />}
                loading={downloads.isPending}
                onClick={() => openArtifact('doc')}
              >
                Submission guide
              </Button>
            )}
            <Text size="xs" c="dimmed">
              Links are private to you and expire after 15 minutes.
            </Text>
          </Group>
        )}

        {/* B7: the portal facts, from the same source as the downloadable guide */}
        {submission.submissionDoc && (
          <Stack gap={4}>
            <Button
              size="xs"
              variant="subtle"
              px={0}
              leftSection={walkthroughOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              onClick={() => setWalkthroughOpen((o) => !o)}
              style={{ alignSelf: 'flex-start' }}
            >
              Portal walkthrough
            </Button>
            <Collapse in={walkthroughOpen}>
              <Card withBorder radius="sm" p="sm" bg="var(--mantine-color-default-hover)">
                <MarkdownPreview source={submission.submissionDoc} />
              </Card>
            </Collapse>
          </Stack>
        )}

        {/* Deep-link test values */}
        {deeplinks.length > 0 && (
          <Stack gap={4}>
            <Text fw={600} size="sm">
              Deep Linking Test Values
            </Text>
            <Text size="xs" c="dimmed">
              Enter these in the portal&apos;s Deep Linking section — each one was
              launch-verified on a real device during preflight.
            </Text>
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>mediaType</Table.Th>
                  <Table.Th>contentId</Table.Th>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Verified</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {deeplinks.map((d) => (
                  <Table.Tr key={`${d.mediaType}-${d.contentId}`}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {d.mediaType}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {d.contentId}
                      </Text>
                    </Table.Td>
                    <Table.Td>{d.title}</Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={d.pass ? 'green' : 'red'}>
                        {d.pass ? 'Pass' : 'Fail'}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {staleWarning && (
              <Text size="sm" c="orange.7">
                {staleWarning}
              </Text>
            )}
            {canRefresh && (
              <Group gap="sm" align="center">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconRefresh size={14} />}
                  onClick={refreshGuide}
                  loading={transition.isPending}
                >
                  Refresh the store guide
                </Button>
                <Text size="xs" c="dimmed">
                  Re-tests the values on a device against your current content; about a minute.
                </Text>
              </Group>
            )}
          </Stack>
        )}

        {/* Portal-entry checklist */}
        <Stack gap={4}>
          <Group gap="xs">
            <Text fw={600} size="sm">
              Portal Entry Checklist
            </Text>
            {gaps.length > 0 && (
              <Badge size="sm" variant="light" color="orange">
                {gaps.length} to fill
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            The {storeLabel} portal asks for these during submission. Fill blanks{' '}
            {isRoku ? 'on the Store Listing tab' : 'in Store Setup (the Store Listing step)'} before
            submitting.
          </Text>
          <Table withTableBorder>
            <Table.Tbody>
              {checklist.map((c) => (
                <Table.Tr key={c.label}>
                  <Table.Td w={180}>
                    <Text size="sm">{c.label}</Text>
                  </Table.Td>
                  <Table.Td>
                    {c.value ? (
                      <Text size="sm" style={{ wordBreak: 'break-all' }}>
                        {c.value}
                      </Text>
                    ) : c.hint ? (
                      <Tooltip label={c.hint}>
                        <Badge size="sm" variant="light" color="gray">
                          Not set
                        </Badge>
                      </Tooltip>
                    ) : (
                      <Badge size="sm" variant="light" color="orange">
                        Missing
                      </Badge>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      </Stack>
    </Card>
  );
}
